import { useQuery } from '@tanstack/react-query'
import { Flex, Image, PaperClipIcon, Spinner, Text, Tooltip, XMarkIcon } from 'components'
import { useState } from 'react'
import { MediaStatus } from 'types'
import { isImagePath } from 'utils'
import { getSessionMedia } from '../../../utils'

interface Props {
  eventId: string
  sessionId: string
  workspaceId: string
  mediaStatus: MediaStatus
  onClick: () => void
}

export default function LeadTableSessionMediaPreview({
  eventId,
  sessionId,
  workspaceId,
  mediaStatus,
}: Props) {
  const [open, setOpen] = useState(false)
  const { data, isFetching } = useQuery({
    queryKey: ['sessions', workspaceId, eventId, sessionId],
    queryFn: () => getSessionMedia(workspaceId, sessionId, eventId),
    enabled: open,
  })
  const images = (data ?? []).filter(({ path }) => isImagePath(path))

  switch (mediaStatus) {
    case 'uploaded': {
      return (
        <Tooltip
          onHoverChange={setOpen}
          content={
            <Flex direction="column" gap={3} p={4} w="min(300px, 40vw)">
              {isFetching ? (
                <Spinner />
              ) : images.length === 0 ? (
                <Text textStyle="bodySmall" opacity={0.7}>
                  No images
                </Text>
              ) : (
                <Flex direction="column" align="center" gap={2}>
                  <Image
                    aria-label="Session attachment"
                    key={images[0].path}
                    objectFit="fit"
                    borderRadius="md"
                    src={images[0].url}
                  />
                  {images.length > 1 && (
                    <Text textStyle="bodySmall" opacity={0.7}>
                      +{images.length - 1} {`image${images.length > 2 ? 's' : ''}`}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
          }
        >
          <PaperClipIcon type="outline" />
        </Tooltip>
      )
    }

    case 'pending': {
      return (
        <Tooltip content="Media is being uploaded">
          <Spinner />
        </Tooltip>
      )
    }
    case 'failed': {
      return (
        <Tooltip content="Media upload failed">
          <XMarkIcon type="outline" color="red.500" />
        </Tooltip>
      )
    }
  }
}
